import type { Ref } from 'vue'
import { scheduleApi, taskStageFromTaskStatus } from '@/api/schedule'
import type {
  ScheduleSnapshotDetail,
  ScheduleSnapshotSummary,
  ScheduleTask,
  ScheduleTaskDetail,
  ScheduleTaskStatus
} from '@/types'
import {
  applySnapshotStatusToDetail,
  applySnapshotStatusToList,
  findPublishedSnapshotId
} from './snapshotHelpers'

type PublishLifecycleDeps = {
  currentTask: Ref<ScheduleTaskDetail | null>
  tasks: Ref<ScheduleTask[]>
  snapshots: Ref<ScheduleSnapshotSummary[]>
  activeSnapshot: Ref<ScheduleSnapshotDetail | null>
}

/**
 * 发布 / 启用相关 actions。逻辑从 schedule store 原样迁出。
 */
export function useSchedulePublishLifecycle(deps: PublishLifecycleDeps): {
  validateForPublish: (snapshotId?: number) => Promise<Awaited<ReturnType<typeof scheduleApi.validateForPublish>> | null>
  publishTask: (snapshotId: number) => Promise<Awaited<ReturnType<typeof scheduleApi.publishTask>> | null>
  unpublishTask: () => Promise<Awaited<ReturnType<typeof scheduleApi.unpublishTask>> | null>
  validateForActivate: () => Promise<Awaited<ReturnType<typeof scheduleApi.validateForActivate>> | null>
  activateTask: (payload?: Parameters<typeof scheduleApi.activateTask>[1]) => Promise<Awaited<ReturnType<typeof scheduleApi.activateTask>> | null>
  deactivateTask: () => Promise<Awaited<ReturnType<typeof scheduleApi.deactivateTask>> | null>
} {
  const { currentTask, tasks, snapshots, activeSnapshot } = deps

  function setCurrentTaskStatus(status: ScheduleTaskStatus): void {
    const stage = taskStageFromTaskStatus(status)
    if (currentTask.value) {
      currentTask.value.status = status
      currentTask.value.stage = stage
      const idx = tasks.value.findIndex((item) => item.id === currentTask.value?.id)
      if (idx !== -1) {
        tasks.value[idx] = {
          ...tasks.value[idx],
          status,
          stage
        }
      }
    }
  }

  function applySnapshotStatus(status: ScheduleTaskStatus, publishedSnapshotId: number | null): void {
    snapshots.value = applySnapshotStatusToList(snapshots.value, status, publishedSnapshotId)
    if (activeSnapshot.value) {
      activeSnapshot.value = applySnapshotStatusToDetail(
        activeSnapshot.value,
        status,
        publishedSnapshotId
      )
    }
  }

  async function validateForPublish(
    snapshotId?: number
  ): Promise<Awaited<ReturnType<typeof scheduleApi.validateForPublish>> | null> {
    if (!currentTask.value) return null
    return scheduleApi.validateForPublish(currentTask.value.id, snapshotId)
  }

  async function publishTask(
    snapshotId: number
  ): Promise<Awaited<ReturnType<typeof scheduleApi.publishTask>> | null> {
    if (!currentTask.value) return null
    const result = await scheduleApi.publishTask(currentTask.value.id, snapshotId)
    const nextStatus = result.status as ScheduleTaskStatus
    setCurrentTaskStatus(nextStatus)
    applySnapshotStatus(nextStatus, result.publishedSnapshotId ?? snapshotId)
    return result
  }

  async function unpublishTask(): Promise<Awaited<ReturnType<typeof scheduleApi.unpublishTask>> | null> {
    if (!currentTask.value) return null
    const result = await scheduleApi.unpublishTask(currentTask.value.id)
    const nextStatus = result.status as ScheduleTaskStatus
    setCurrentTaskStatus(nextStatus)
    applySnapshotStatus(nextStatus, null)
    return result
  }

  async function validateForActivate(): Promise<
    Awaited<ReturnType<typeof scheduleApi.validateForActivate>> | null
  > {
    if (!currentTask.value) return null
    return scheduleApi.validateForActivate(currentTask.value.id)
  }

  async function activateTask(
    payload?: Parameters<typeof scheduleApi.activateTask>[1]
  ): Promise<Awaited<ReturnType<typeof scheduleApi.activateTask>> | null> {
    if (!currentTask.value) return null
    const result = await scheduleApi.activateTask(currentTask.value.id, payload)
    const nextStatus = result.status as ScheduleTaskStatus
    setCurrentTaskStatus(nextStatus)
    applySnapshotStatus(nextStatus, findPublishedSnapshotId(snapshots.value))
    return result
  }

  async function deactivateTask(): Promise<Awaited<ReturnType<typeof scheduleApi.deactivateTask>> | null> {
    if (!currentTask.value) return null
    const result = await scheduleApi.deactivateTask(currentTask.value.id)
    const nextStatus = result.status as ScheduleTaskStatus
    setCurrentTaskStatus(nextStatus)
    applySnapshotStatus(nextStatus, findPublishedSnapshotId(snapshots.value))
    return result
  }

  return {
    validateForPublish,
    publishTask,
    unpublishTask,
    validateForActivate,
    activateTask,
    deactivateTask
  }
}
